import { useEffect, useState } from "react";
import { Button, Modal, ModalBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createAdminCompanyDeviceContract, updateAdminCompanyDeviceContract } from "../../slices/admin/thunk";
import { createSelector } from "@reduxjs/toolkit";

const selectAllDevices = createSelector(
    state => state.Admin.device,
    (deviceObj) => {
        return Object.values(deviceObj)
            .flat()
            .sort((a, b) => a.id - b.id);
    }
);

const AdminDeviceContractModal = ({ show, onCloseClick, data, fetchData }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isShowError, setShowError] = useState(false);
    const isNewContract = data === null;

    const devices = useSelector(selectAllDevices);

    const [deviceContractData, setDeviceContractData] = useState({
        id: '',
        admin_device_id: '',
        start_date: '',
        end_date: '',
        remark: ''
    });

    useEffect(() => {
        if (show) {
            const defaultDeviceContractData = {
                id: '',
                admin_device_id: devices.length > 0 ? devices[0].id : '',
                start_date: '',
                end_date: '',
                remark: ''
            };
            setDeviceContractData({
                ...defaultDeviceContractData,
                ...(data ?? {})
            });
        }
    }, [show, data, devices]);

    const closeModal = () => {
        setShowError(false);
        onCloseClick(deviceContractData);
    };

    // Safe input handler with fallbacks
    const handleChange = (field) => (e) => {
        let value = e.target.value || '';
        setDeviceContractData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Modal isOpen={show} toggle={() => { closeModal(false) }} centered={true} size="lg">
            <div className="modal-header">
                <div className="modal-title">{isNewContract ? t('PageAdmin.Btn.CreateDeviceContract') : t('PageAdmin.Btn.ModifyDeviceContract')}</div>
                <div className="d-flex flex-row modal-close-btn">
                    <Button type="button" className="btn-close btn-sm btn-light" onClick={() => { closeModal(false); }} ></Button>
                </div>
            </div>
            <ModalBody className="d-flex flex-column">
                <form>
                    {!isNewContract &&
                        <div className="row">
                            <div className="col-md-3">
                                <label className="col-form-label" htmlFor="admin-device-id">{t('PageAdmin.CompanyDeviceContractTable.ContractID')}</label>
                            </div>
                            <div className="col-md-9">
                                <input type="text" className="form-control" id="admin-device-id" value={deviceContractData.id} disabled />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="admin-device-record-company">{t('PageAdmin.CompanyDeviceContractTable.DeviceID')}</label>
                        </div>
                        <div className="col-md-9">
                            <select
                                className="form-control"
                                id="admin-record-device-company"
                                value={deviceContractData.admin_device_id}
                                onChange={handleChange("admin_device_id")}
                                disabled={!isNewContract}
                            >
                                {
                                    devices.map(device => (
                                        <option key={device.id} value={device.id}>{device.id}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="start-date">{t('PageAdmin.CompanyDeviceContractTable.StartDatetime')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="date" className="form-control" id="start-date" value={deviceContractData["start_date"]} onChange={handleChange("start_date")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="end-date">{t('PageAdmin.CompanyDeviceContractTable.EndDatetime')}</label>
                        </div>
                        <div className="col-md-9">
                            <input type="date" className="form-control" id="end-date" value={deviceContractData["end_date"]} onChange={handleChange("end_date")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="admin-device-contract-remark">{t('PageAdmin.CompanyDeviceContractTable.Remark')}</label>
                        </div>
                        <div className="col-md-9">
                            <textarea
                                rows={3}
                                maxLength={500}
                                className="form-control"
                                id="admin-device-contract-remark"
                                value={deviceContractData.remark || ''}
                                onChange={handleChange("remark")}
                                style={{ resize: 'vertical', overflowY: 'auto' }}
                            />
                        </div>
                    </div>
                </form>
                <div className="d-flex flex-column align-items-center company-save-modal-btn-container">
                    {isShowError ? <div className="company-save-modal-error">{t('PageAdmin.SaveCompanyModal.SaveError')}</div> : null}
                    <div className="d-flex flex-row justify-content-center ">
                        <Button className="btn-sm report-save-modal-btn" color="primary" onClick={() => {
                            closeModal(false)
                        }}> {t('PageReport.SaveReportModal.BtnCancel')} </Button>
                        <Button className="btn-sm report-save-modal-btn" color="primary" onClick={async () => {
                            let result = false;
                            if (isNewContract) {
                                result = await dispatch(createAdminCompanyDeviceContract(deviceContractData));
                            }
                            else {
                                result = await dispatch(updateAdminCompanyDeviceContract(deviceContractData));
                            }
                            if (result === true) {
                                fetchData();
                                setShowError(false);
                                closeModal(true);
                            } else {
                                setShowError(true);
                            }
                        }}> {t('PageReport.SaveReportModal.BtnSave')} </Button>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}

export default AdminDeviceContractModal;